'use client';

import { useMemo } from 'react';
import { ArrowRight, Building2, ChevronLeft, ChevronRight, ExternalLink, Layers, Moon, Play, Sun, X } from 'lucide-react';
import { AppLink } from '@/lib/appLink';
import { useCmsData } from '@/lib/CmsContext';

const ICON_BY_SLUG = {
  moon: Moon,
  sun: Sun,
  building2: Building2,
  layers: Layers,
};

const ICONS = [Moon, Sun, Building2, Layers];

function resolveWorkIcon(slug, fallbackIndex = 0) {
  if (slug && ICON_BY_SLUG[slug]) return ICON_BY_SLUG[slug];
  return ICONS[fallbackIndex % ICONS.length];
}

function isVideoUrl(src = '') {
  return /\.(mp4|webm|ogg|mov)(\?|$)/i.test(src);
}

function normalizeLocKey(value = '') {
  return String(value).trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * @param {import('@/types/cms').WorkItem[]} works
 * @param {import('@/types/cms').WorksGalleryPageSection} section
 */
function pickWorks(works, section) {
  if (!Array.isArray(works) || !works.length) return [];

  const ids = Array.isArray(section.work_ids) ? section.work_ids.map(Number) : [];
  if (ids.length) {
    const byWpId = new Map(works.map((w) => [Number(w.wp_id || w.id), w]));
    return ids.map((id) => byWpId.get(id)).filter(Boolean);
  }

  let list = works;

  const geo = section.geo_area;
  if (geo) {
    list = list.filter((w) => Array.isArray(w.geo_area) && w.geo_area.includes(geo));
  }

  // Client type pills replace hard catalog filter when enabled.
  const showType = section.show_type_filter === true;
  const line = section.catalog_line;
  if (line && !showType) {
    list = list.filter((w) => Array.isArray(w.catalog_line) && w.catalog_line.includes(line));
  }

  return list;
}

const CATALOG_TYPE_CHIPS = [
  { id: 'all', type: 'all', value: 'all', label: 'Всі' },
  { id: 'construction', type: 'catalog', value: 'construction', label: 'Будинок' },
  { id: 'facade', type: 'catalog', value: 'facade', label: 'Фасад' },
  { id: 'repair', type: 'catalog', value: 'repair', label: 'Ремонт' },
];

/**
 * Geo city/oblast labels from CMS — used to avoid duplicating them as locality chips.
 * @param {import('@/types/cms').GeoOblast[]} geoArea
 */
function buildGeoLookup(geoArea = []) {
  /** @type {Map<string, string>} */
  const slugToLabel = new Map();
  /** @type {Set<string>} */
  const regionNameKeys = new Set();

  geoArea.forEach((oblast) => {
    const oblastName = oblast.oblast || oblast.short || '';
    if (oblastName) regionNameKeys.add(normalizeLocKey(oblastName));
    if (oblast.short) regionNameKeys.add(normalizeLocKey(oblast.short));

    (oblast.cities || []).forEach((city) => {
      if (!city?.slug) return;
      const label = city.name || city.display_name || city.slug;
      slugToLabel.set(city.slug, label);
      regionNameKeys.add(normalizeLocKey(city.name || ''));
      regionNameKeys.add(normalizeLocKey(city.display_name || ''));
      const bare = String(city.display_name || '').replace(/^м\.\s*/i, '').trim();
      if (bare) regionNameKeys.add(normalizeLocKey(bare));
    });
  });

  return { slugToLabel, regionNameKeys };
}

/**
 * Location/region pills.
 * When scoped to a geo_area → localities only.
 * Otherwise → cities + localities.
 *
 * @param {import('@/types/cms').WorkItem[]} works
 * @param {import('@/types/cms').GeoOblast[]} geoArea
 * @param {string} [scopedGeo]
 */
function buildLocationFilterChips(works, geoArea, scopedGeo = '') {
  const { slugToLabel, regionNameKeys } = buildGeoLookup(geoArea);
  /** @type {Map<string, string>} */
  const cityChips = new Map();
  /** @type {Map<string, string>} */
  const locChips = new Map();

  works.forEach((work) => {
    if (!scopedGeo) {
      (work.geo_area || []).forEach((slug) => {
        if (!slug || cityChips.has(slug)) return;
        cityChips.set(slug, slugToLabel.get(slug) || slug);
      });
    }

    const loc = String(work.location_label || '').trim();
    if (!loc) return;
    const key = normalizeLocKey(loc);
    if (!key || regionNameKeys.has(key) || locChips.has(key)) return;
    locChips.set(key, loc);
  });

  const chips = [{ id: 'place-all', type: 'all', value: 'all', label: 'Всі' }];

  if (!scopedGeo) {
    [...cityChips.entries()]
      .sort((a, b) => a[1].localeCompare(b[1], 'uk'))
      .forEach(([slug, label]) => {
        chips.push({ id: `geo:${slug}`, type: 'geo', value: slug, label });
      });
  }

  [...locChips.entries()]
    .sort((a, b) => a[1].localeCompare(b[1], 'uk'))
    .forEach(([key, label]) => {
      chips.push({ id: `loc:${key}`, type: 'loc', value: key, label });
    });

  return chips;
}

/**
 * Type chips present among works (always include Всі + known lines that appear).
 * @param {import('@/types/cms').WorkItem[]} works
 */
function buildTypeFilterChips(works) {
  const present = new Set();
  works.forEach((w) => {
    (w.catalog_line || []).forEach((slug) => present.add(slug));
  });
  return CATALOG_TYPE_CHIPS.filter((chip) => chip.value === 'all' || present.has(chip.value));
}

/**
 * Full media list for one work object (cover first, then gallery, unique).
 * @param {import('@/types/cms').WorkItem} work
 */
function workMediaGallery(work) {
  const urls = [];
  if (work?.cover) urls.push(work.cover);
  if (Array.isArray(work?.gallery)) {
    work.gallery.forEach((url) => {
      if (url && !urls.includes(url)) urls.push(url);
    });
  }
  return urls;
}

/**
 * Masonry grid: one preview tile per work object (not every photo).
 * Full gallery stays in data-gallery for lightbox.
 * @param {import('@/types/cms').WorkItem[]} works
 */
function mapWorksToMasonryPreviews(works) {
  return works
    .map((work) => {
      const gallery = workMediaGallery(work);
      const src = gallery[0] || '';
      return {
        src,
        label: work.title || work.location_label || '',
        gallery,
        photoCount: gallery.length,
        catalog: Array.isArray(work.catalog_line) ? work.catalog_line.filter(Boolean).join(' ') : '',
        geo: Array.isArray(work.geo_area) ? work.geo_area.filter(Boolean).join(' ') : '',
        loc: normalizeLocKey(work.location_label || ''),
      };
    })
    .filter((item) => item.src);
}

function WorksLightbox() {
  return (
    <div
      className="grv-works-lightbox fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      data-grv-works-lightbox
      hidden
      aria-hidden="true"
    >
      <button
        type="button"
        data-grv-works-lb-close
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-card border border-border flex items-center justify-center text-foreground hover:border-gold hover:text-gold transition-colors"
        aria-label="Закрити"
      >
        <X className="w-5 h-5 pointer-events-none" />
      </button>
      <button
        type="button"
        data-grv-works-lb-prev
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-card border border-border flex items-center justify-center text-foreground hover:border-gold hover:text-gold transition-colors"
        aria-label="Попереднє"
      >
        <ChevronLeft className="w-5 h-5 pointer-events-none" />
      </button>
      <button
        type="button"
        data-grv-works-lb-next
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-card border border-border flex items-center justify-center text-foreground hover:border-gold hover:text-gold transition-colors"
        aria-label="Наступне"
      >
        <ChevronRight className="w-5 h-5 pointer-events-none" />
      </button>
      <div className="max-w-2xl w-full max-h-[90vh] flex flex-col items-center gap-3">
        <img
          data-grv-works-lb-img
          alt=""
          className="max-h-[80vh] w-full object-contain rounded-xl"
          hidden
        />
        <video
          data-grv-works-lb-video
          controls
          playsInline
          className="max-h-[80vh] w-full rounded-xl bg-black"
          hidden
        />
        <p className="text-white/70 text-sm" data-grv-works-lb-caption />
      </div>
    </div>
  );
}

function FilterChipRow({ label, group, chips }) {
  if (!chips?.length || chips.length <= 1) return null;

  return (
    <div className="flex flex-wrap gap-2 items-center" data-grv-works-filter-group={group}>
      <span className="text-xs text-muted-foreground uppercase tracking-wider mr-1">{label}</span>
      {chips.map((chip, i) => (
        <button
          key={chip.id}
          type="button"
          data-grv-works-filter
          data-filter-group={group}
          data-filter-type={chip.type}
          data-filter-value={chip.value}
          className={`grv-works-filter-btn px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide border border-border text-muted-foreground transition-all hover:border-gold/50 hover:text-gold${
            i === 0 ? ' is-active' : ''
          }`}
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}

function WorksFilterBar({ typeChips, locationChips }) {
  const hasType = typeChips?.length > 1;
  const hasLoc = locationChips?.length > 1;
  if (!hasType && !hasLoc) return null;

  return (
    <div className="flex flex-wrap gap-3 mb-8" data-grv-works-filters>
      <FilterChipRow label="Тип:" group="catalog" chips={typeChips} />
      {hasType && hasLoc ? <div className="w-px bg-border hidden sm:block mx-2 self-stretch min-h-[28px]" /> : null}
      <FilterChipRow
        label={hasType ? 'Локація:' : 'Регіон:'}
        group="place"
        chips={locationChips}
      />
    </div>
  );
}

/**
 * @param {{ section?: import('@/types/cms').WorksGalleryPageSection }} props
 */
export default function WorksGallery({ section = {} }) {
  const cms = useCmsData();
  const layout = section.layout === 'masonry' ? 'masonry' : 'carousel';
  const showTypeFilter = section.show_type_filter === true;
  const showLocationFilter =
    section.show_location_filter === true || section.show_region_filter === true;

  const works = useMemo(
    () => pickWorks(cms?.work_item || [], section),
    [cms?.work_item, section],
  );

  const typeChips = useMemo(
    () => (showTypeFilter ? buildTypeFilterChips(works) : []),
    [showTypeFilter, works],
  );

  const locationChips = useMemo(
    () =>
      showLocationFilter
        ? buildLocationFilterChips(works, cms?.geo_area || [], section.geo_area || '')
        : [],
    [showLocationFilter, works, cms?.geo_area, section.geo_area],
  );

  const carouselItems = useMemo(
    () =>
      works
        .map((work, i) => {
          const gallery = workMediaGallery(work);
          const tags = Array.isArray(work.photo_labels)
            ? work.photo_labels.map((t) => String(t || '').trim()).filter(Boolean)
            : [];
          if (!tags.length && work.location_label) tags.push(work.location_label);
          if (!tags.length && work.catalog_line?.[0]) tags.push(work.catalog_line[0]);

          return {
            src: gallery[0] || '',
            gallery,
            label: work.title || work.location_label || '',
            tags,
            Icon: resolveWorkIcon(work.card_icon, i),
            catalog: Array.isArray(work.catalog_line) ? work.catalog_line.filter(Boolean).join(' ') : '',
            geo: Array.isArray(work.geo_area) ? work.geo_area.filter(Boolean).join(' ') : '',
            loc: normalizeLocKey(work.location_label || ''),
          };
        })
        .filter((item) => item.src),
    [works],
  );

  const masonryItems = useMemo(() => mapWorksToMasonryPreviews(works), [works]);

  const items = layout === 'masonry' ? masonryItems : carouselItems;
  if (!items.length) return null;

  const eyebrow = section.eyebrow || (layout === 'masonry' ? 'Наші роботи' : 'Фасади · Портфоліо');
  const title = section.title || (layout === 'masonry' ? 'Фотогалерея' : 'Наші фасадні роботи');
  const ctaLabel = (section.cta_label || '').trim();
  const ctaHref = section.cta_href || '/our-works';
  const showCta = Boolean(ctaLabel);
  const hasFilters = typeChips.length > 1 || locationChips.length > 1;

  return (
    <section
      className="grv-works bg-background py-20 lg:py-28 border-t border-border"
      data-grv-works
      data-grv-works-layout={layout}
      data-grv-works-index="0"
      data-grv-works-count={String(items.length)}
      data-grv-works-has-filter={hasFilters ? '1' : '0'}
      data-grv-works-filter-catalog="all"
      data-grv-works-filter-place-type="all"
      data-grv-works-filter-place-value="all"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {layout === 'masonry' ? (
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-semibold uppercase tracking-widest">{eyebrow}</span>
              <div className="w-8 h-px bg-gold" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-foreground mb-4">{title}</h2>
            <div className="gold-divider" />
          </div>
        ) : (
          <div className="flex items-center justify-between mb-10">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-6 h-px bg-gold" />
                <span className="text-gold text-xs font-semibold uppercase tracking-widest">{eyebrow}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-foreground">{title}</h2>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-grv-works-prev
                className="grv-works-nav w-10 h-10 rounded-full border border-border flex items-center justify-center text-foreground hover:border-gold hover:text-gold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Попередні"
                disabled
              >
                <ChevronLeft className="w-5 h-5 pointer-events-none" />
              </button>
              <button
                type="button"
                data-grv-works-next
                className="grv-works-nav w-10 h-10 rounded-full border border-border flex items-center justify-center text-foreground hover:border-gold hover:text-gold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Наступні"
              >
                <ChevronRight className="w-5 h-5 pointer-events-none" />
              </button>
            </div>
          </div>
        )}

        {hasFilters ? <WorksFilterBar typeChips={typeChips} locationChips={locationChips} /> : null}

        <p className="text-center py-12 text-muted-foreground" data-grv-works-empty hidden>
          Обʼєктів не знайдено
        </p>

        {layout === 'masonry' ? (
          <div className="columns-2 sm:columns-3 lg:columns-4 gap-3" data-grv-works-grid>
            {items.map((item, i) => {
              const isVideo = isVideoUrl(item.src);
              return (
                <div
                  key={`work-${item.loc || 'x'}-${item.src}-${i}`}
                  role="button"
                  tabIndex={0}
                  data-grv-works-slide
                  data-grv-works-open
                  data-index={String(i)}
                  data-src={item.src}
                  data-label={item.label}
                  data-gallery={JSON.stringify(item.gallery)}
                  data-catalog={item.catalog || ''}
                  data-geo={item.geo || ''}
                  data-loc={item.loc || ''}
                  className="relative overflow-hidden rounded-xl cursor-pointer group border border-border hover:border-gold/50 transition-colors mb-3 break-inside-avoid"
                >
                  {isVideo ? (
                    <video
                      src={item.src}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-auto object-cover"
                    />
                  ) : (
                    <img
                      src={item.src}
                      alt={item.label}
                      className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  {item.photoCount > 1 ? (
                    <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-lg border border-white/10 pointer-events-none">
                      {item.photoCount} фото
                    </div>
                  ) : null}
                  {isVideo ? (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-12 h-12 rounded-full bg-gold/90 border border-gold flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <Play className="w-5 h-5 text-dark fill-dark ml-0.5" />
                      </div>
                    </div>
                  ) : (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-end">
                      {item.label ? (
                        <span className="text-white text-xs font-semibold px-3 pb-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          {item.label}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <>
            <div className="grv-works-viewport overflow-hidden" data-grv-works-viewport>
              <div className="grv-works-track flex" data-grv-works-track>
                {items.map((item, i) => {
                  const Icon = item.Icon;
                  const isVideo = isVideoUrl(item.src);
                  return (
                    <div
                      key={`${item.src}-${i}`}
                      role="button"
                      tabIndex={0}
                      data-grv-works-slide
                      data-grv-works-open
                      data-index={String(i)}
                      data-src={item.src}
                      data-label={item.label}
                      data-gallery={JSON.stringify(item.gallery)}
                      data-catalog={item.catalog || ''}
                      data-geo={item.geo || ''}
                      data-loc={item.loc || ''}
                      className="grv-works-slide flex-none cursor-pointer group"
                    >
                      <div
                        className="relative rounded-2xl overflow-hidden border border-border hover:border-gold/50 transition-colors bg-card"
                        style={{ aspectRatio: '3/4' }}
                      >
                        {isVideo ? (
                          <video
                            src={item.src}
                            muted
                            playsInline
                            preload="metadata"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <img
                            src={item.src}
                            alt={item.label}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        )}
                        <div className="absolute top-4 left-4 w-10 h-10 bg-gold/20 border border-gold/40 rounded-lg flex items-center justify-center backdrop-blur-sm shrink-0">
                          <Icon className="w-5 h-5 text-gold" />
                        </div>
                        {item.tags?.length ? (
                          <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5 max-w-[65%]">
                            {item.tags.map((tag, ti) => (
                              <div
                                key={`${tag}-${ti}`}
                                className="bg-dark/70 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full border border-white/10"
                              >
                                {tag}
                              </div>
                            ))}
                          </div>
                        ) : null}
                        {isVideo ? (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-14 h-14 rounded-full bg-gold/90 border border-gold flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                              <Play className="w-6 h-6 text-dark fill-dark ml-0.5" />
                            </div>
                          </div>
                        ) : (
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center pointer-events-none">
                            <ExternalLink className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                          <p className="text-white text-sm font-semibold">{item.label}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-center gap-2 mt-6" data-grv-works-dots>
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  data-grv-works-dot
                  data-index={String(i)}
                  aria-label={`Слайд ${i + 1}`}
                  className="grv-works-dot rounded-full transition-all duration-300"
                />
              ))}
            </div>
          </>
        )}

        {showCta ? (
          <div className="mt-10 text-center">
            <AppLink
              href={ctaHref}
              className="inline-flex items-center gap-2 border border-gold/50 text-gold hover:bg-gold hover:!text-black font-bold px-8 py-3.5 rounded-lg transition-all duration-300 text-sm uppercase tracking-wider"
            >
              {ctaLabel} <ArrowRight className="w-4 h-4" />
            </AppLink>
          </div>
        ) : null}
      </div>

      <WorksLightbox />
    </section>
  );
}
